import { Button, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import LabeledReportStatus from 'components/molecules/labeledStatus/LabeledReportStatus';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCancelPeriodReport,
  useGetPeriodReportsOrders,
  usePostPeriodReport,
} from 'services/label-report';
import {
  IPeriodReportMutationForm,
  REPORT_GENERATION_STATUS,
} from 'services/label-report/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate } from 'utils/global/date.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { boolean, object, string } from 'yup';
import { usePermissionGuard } from "hooks/usePermissionGuard";

enum PERIODREPORTS_COLUMNS {
  settlementPeriod,
  plannedStartDate,
  status,
}

const periodReportInitialValue: IPeriodReportMutationForm = {
  settlementPeriodId: '',
  planedStartDate: '',
  asap: false,
};

const periodReportValidator = object().shape({
  settlementPeriodId: string().required(i18next.t('formik-error.required')),
  planedStartDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean(),
});

const PeriodReports = () => {
  usePermissionGuard('SettlementPeriodPermissionsLIST**');

  const getSettelmentPeriod = useGetSettlementLightList();
  const getPeriodReports = useGetPeriodReportsOrders();

  const createPeriodReport = usePostPeriodReport();
  const cancelPeriodReport = useCancelPeriodReport();

  const tableProps = useInfiniteTable(getPeriodReports, { selectable: false });

  const { t } = useTranslation();

  const i18nextPrefix = 'period-reports.list';

  const columnsVisibility = useTableColumnsVisibility(
    'PeriodReports',
    PERIODREPORTS_COLUMNS
  );

  const getSettlementPeriodOptions = () =>
    getSettelmentPeriod.data?.flat().map(settelment => ({
      value: settelment.id,
      label: getSettlementPeriodName(settelment.settlementPeriodName),
    }));

  const onSubmit = async (
    values: IPeriodReportMutationForm,
    helpers: FormikHelpers<IPeriodReportMutationForm>,
    actions: PopupActions | null
  ) => {
    await createPeriodReport.mutateAsync(
      {
        ...values,
        planedStartDate: values.planedStartDate.length
          ? new Date(values.planedStartDate).toISOString()
          : null,
      },
      {
        onSuccess: async () => {
          await getPeriodReports.refetch();
          actions?.close();
        },
      }
    );
  };

  const cancelPeriodReportFuncion = async (
    id: string,
    actions: PopupActions
  ) => {
    await cancelPeriodReport.mutateAsync(id, {
      onSuccess: async () => {
        await getPeriodReports.refetch();
        actions.close();
      },
      onError: async error => {
        onErrorShowAllErrorsToast(error);
        await getPeriodReports.refetch();
      },
    });
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=''
          topbarContent={
            <>
              <div className='flex gap-2'>
                <Table.CustomColumnsButton
                  columns={PERIODREPORTS_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
                <MagellanPopups.Form
                  header={t('period-reports.popup.header')}
                  trigger={
                    <Button type='button' variant={BUTTON_VARIANT.submit}>
                      <Button.Text text={t('button.create')} />
                    </Button>
                  }
                  initialValues={periodReportInitialValue}
                  validationSchema={periodReportValidator}
                  onSubmit={onSubmit}>
                  <FormInput.Select
                    label={t('input.label.settlementPeriod')}
                    name='settlementPeriodId'
                    options={getSettlementPeriodOptions()}
                    isAsync
                  />
                  <FormInput.Text
                    label={t('input.label.plannedStartDate')}
                    type='datetime-local'
                    name='planedStartDate'
                  />
                  <FormInput.Checkbox
                    name='asap'
                    label={t('input.label.asap')}
                  />
                </MagellanPopups.Form>
              </div>
            </>
          }></TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        {...tableProps}
        size='compact'
        columns={[
          {
            label: t(`input.label.settlementPeriod`),
            renderCell: ({ settlementPeriod }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(
                    settlementPeriod.settlementPeriodName
                  )}
                />
                <TableItem.Text
                  isSupplementary
                  text={`${
                    settlementPeriod.startDate &&
                    formatDate(settlementPeriod.startDate)
                  } / ${
                    settlementPeriod.endDate &&
                    formatDate(settlementPeriod.endDate)
                  }`}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              PERIODREPORTS_COLUMNS.settlementPeriod
            ),
          },
          {
            label: `${t(`input.label.plannedStartDate`)}`,
            renderCell: ({ plannedStartDate }) => (
              <TableItem>
                <TableItem.Text text={formatDate(plannedStartDate, true)} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              PERIODREPORTS_COLUMNS.plannedStartDate
            ),
          },
          {
            label: t(`input.label.status`),
            renderCell: ({ reportGenerationStatusId }) => (
              <TableItem>
                <LabeledReportStatus status={reportGenerationStatusId} />
              </TableItem>
            ),
            resize: false,
            hide: columnsVisibility.hide(PERIODREPORTS_COLUMNS.status),
          },
          {
            label: '',
            renderCell: ({ id, reportGenerationStatusId }) => (
              <TableItem justifyPosition='end'>
                <div style={{ display: 'flex' }}>
                  {reportGenerationStatusId ===
                    REPORT_GENERATION_STATUS.Waiting && (
                    <MagellanPopups.CancelWarning
                      name={t('period-reports.what')}
                      onCancel={actions =>
                        cancelPeriodReportFuncion(id, actions)
                      }
                    />
                  )}
                </div>
              </TableItem>
            ),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default PeriodReports;
